import { Component, Input, OnInit } from '@angular/core';
import * as moment from 'moment';
import { ColumnsConfig, TableConfig, TableData } from 'sct-custom-table/sct-table/projects/sct-table/src/lib/custom-table-interface';
import { TranslateService } from '@ngx-translate/core';
import { UsersService } from 'src/app/users/users.service';
import { SitesService } from 'src/app/sites/sites.service';
import { CommonService } from 'src/app/shared/services/common.service';
import { DeviceService } from '../site-dashboard/device/device.service';
import { Router } from '@angular/router';

@Component({
	selector: 'app-im-usage-report',
	templateUrl: './im-usage-report.component.html',
	styleUrls: ['./im-usage-report.component.css']
})
export class ImUsageReportComponent {
	constructor(
		private translateService: TranslateService,
		private usersService: UsersService,
		public sitesService: SitesService,
		public commonService: CommonService,
		public deviceService: DeviceService,
		private router: Router,
	){}

	@Input() siteInfo: any = {};
	@Input() devices: any = [];

	selectedYearUsageReport!: number;
	selectedQuarterUsageReport!: string;
	quarterlyUsageReportDataIsEmpty = false;
	quarterlyUsageReportData: any;
	selectedQuarterUsageReportData: any = {};

	yearsOption: any[] = [];
	quartersOption: any[] = [];
	minDate!: Date;
	siteId: number;
	customerId: number;
	siteReport: any;

	imUsageReportTableConfig = {
		column: [] as ColumnsConfig[],
		data: [],
		config: {
			hasExport: true,
			hasPagination: true,
			draggable: true,
			pageSize: 100,
			fileName: this.translateService.instant('iotemeter.site.usage_report')
		} as TableConfig
	};

	ngOnInit(): void {
		this.siteId = this.siteInfo.id;
		this.customerId = this.siteInfo.customer_id;
		const minDateDevices = Math.min(...this.devices.map(device => device._installation_date_ms));
		const minDate = moment((minDateDevices || 1));
		this.minDate = minDate.toDate();

		const startYear = minDate.year();
		const currentYear = moment().year();
		this.selectedYearUsageReport = currentYear;
		const yearsOption = [];
		for (let year = currentYear; year >= startYear; year--) {
			yearsOption.push(year);
		}
		this.yearsOption = yearsOption;
	
		this.sitesService.getSiteReportsIotemeterData(this.siteId).subscribe((res: any) => {
			this.siteReport = res;
			this.quarterlyUsageReportDataIsEmpty = (res.quarterlyUsageReport && Object.keys(res.quarterlyUsageReport).length === 0) || false;
			this.preparecolumnConfig();
			this.selectQuarter(`Q${moment().quarter()}`);
		});
	}

	selectQuarter(quarterSelected: string) {
		this.quartersOption = this.getListQuarters(this.selectedYearUsageReport);
		this.selectedQuarterUsageReport = quarterSelected;
		this.updateSelectedQuarter();
	}


	updateSelectedQuarter() {
		const quarter = `${this.selectedYearUsageReport}_${this.selectedQuarterUsageReport}`;
		this.prepareUsageData(quarter);
	}

	getListQuarters(selectedYear: number) {
		if (selectedYear === moment().year() || selectedYear === moment(this.minDate).year()) {
			const quarters: string[] = ['All-Year'];
			for (let quarter = 1; quarter <= 4; quarter++) {
				if ((selectedYear === moment().year() && quarter > moment().quarter()) || (quarter < moment(this.minDate).quarter() && selectedYear === moment(this.minDate).year()))
					continue;
				quarters.push(`Q${quarter}`);
			}
			return quarters;
		}
		return ['All-Year', 'Q1', 'Q2', 'Q3', 'Q4'];
	}

	prepareUsageData(quarter: string) {
		this.quarterlyUsageReportData = [];
		const installedDevices = this.siteReport.installedDevices;

		const aggregateDataForYear = {
			total_KWHRS: 0,
			daily_KWHRS: 0,
			total_consumed_usage_time: 0,
			generated_energy_duration: 0,
		};

		const data: TableData[] = [];

		if (quarter.includes('All-Year')) {
			for (const device of installedDevices) {

				const fullYearData = {
					billed_kwhrs: 0,
					total_KWHRS: 0,
					daily_KWHRS: 0,
					daily_billed_kwhrs: 0,
					negative_peak_power: 0,
					total_consumed_usage_time: 0,
					total_generated_usage_time: 0,
					consumed_energy_duration: 0,
					generated_energy_duration: 0,
					consumed_energy: 0,
					generated_energy: 0,
				};

				const quarterData = device.quarters_data || {};

				for (const key in quarterData) {
					if (
						quarterData.hasOwnProperty(key) &&
						+key.split('_')[0] === this.selectedYearUsageReport
					) {
						const quarterObj = quarterData[key] || {};
						const year = +key.split('_')[0];
						const quarterIndex = +key.split('_')[1].replace('Q', '');
						const days = this.commonService.getDaysInQuarter(year, quarterIndex);

						fullYearData.total_KWHRS += quarterObj.billed_kwhrs || 0;
						fullYearData.daily_KWHRS += (quarterObj.billed_kwhrs || 0) / days;
						fullYearData.billed_kwhrs += quarterObj.billed_kwhrs || 0;
						fullYearData.daily_billed_kwhrs += quarterObj.daily_billed_kwhrs || 0;
						fullYearData.negative_peak_power += quarterObj.negative_peak_power || 0;
						fullYearData.total_consumed_usage_time += quarterObj.total_consumed_usage_time || 0;
						fullYearData.total_generated_usage_time += quarterObj.total_generated_usage_time || 0;
						fullYearData.consumed_energy_duration += quarterObj.consumed_energy_duration || 0;
						fullYearData.generated_energy_duration += quarterObj.generated_energy_duration || 0;
						fullYearData.consumed_energy += quarterObj.consumed_energy || 0;
						fullYearData.generated_energy += quarterObj.generated_energy || 0;
					}
				}

				aggregateDataForYear.total_KWHRS += fullYearData.total_KWHRS;
				aggregateDataForYear.daily_KWHRS += fullYearData.daily_KWHRS;
				aggregateDataForYear.total_consumed_usage_time += fullYearData.total_consumed_usage_time;
				aggregateDataForYear.generated_energy_duration += fullYearData.generated_energy_duration;

				device.daily_billed_kwhrs = fullYearData.daily_billed_kwhrs;
				device.billed_kwhrs = fullYearData.billed_kwhrs;
				device.negative_peak_power = fullYearData.negative_peak_power;
				device.total_consumed_usage_time = fullYearData.total_consumed_usage_time;
				device.total_generated_usage_time = fullYearData.total_generated_usage_time;
				device.total_consumed_usage_time_formatted =
					this.commonService.convertSecondsToHoursMinsSecs(device.total_consumed_usage_time || 0);
				device.total_generated_usage_time_time_formatted =
					this.commonService.convertSecondsToHoursMinsSecs(device.total_generated_usage_time || 0);

				data.push(this.mapDeviceToUsageRow(device));
			}

			this.siteReport.quarterlyUsageReport[quarter] = aggregateDataForYear;

		} else {
			installedDevices.forEach((device: any) => {
				const quarterObj = (device.quarters_data || {})[quarter] || {};

				device.billed_kwhrs = quarterObj.billed_kwhrs || 0;
				device.daily_billed_kwhrs = quarterObj.daily_billed_kwhrs || 0;
				device.negative_peak_power = quarterObj.negative_peak_power || 0;
				device.total_consumed_usage_time = quarterObj.total_consumed_usage_time || 0;
				device.total_generated_usage_time = quarterObj.total_generated_usage_time || 0;
				device.consumed_energy_duration = quarterObj.consumed_energy_duration || 0;
				device.generated_energy_duration = quarterObj.generated_energy_duration || 0;
				device.consumed_energy = quarterObj.consumed_energy || 0;
				device.generated_energy = quarterObj.generated_energy || 0;
				device.total_consumed_usage_time_formatted =
					this.commonService.convertSecondsToHoursMinsSecs(device.total_consumed_usage_time || 0);
				device.total_generated_usage_time_time_formatted =
					this.commonService.convertSecondsToHoursMinsSecs(device.total_generated_usage_time || 0);

				data.push(this.mapDeviceToUsageRow(device));
			});
		}

		this.imUsageReportTableConfig.data = data;
		this.selectedQuarterUsageReportData = this.siteReport.quarterlyUsageReport[quarter] || {};
	}


	mapDeviceToUsageRow(device: any): TableData {
		return {
			serial_number: {
				value: device.serial_number,
				link: device.serial_number
					? ['/', this.customerId, this.siteId, device.mac_address, 'performance']
					: null
			},
			panel_id: { value: device.power_view_sn },
			load_id: { value: device.power_view_id },
			installation_date: {
				value: device.installation_date
					? moment(device.installation_date * 1000).format('MM/DD/YYYY')
					: null
			},
			load_type: { value: device.load_type },
			billed_kwhrs: { value: device.billed_kwhrs != null ? Number(device.billed_kwhrs).toLocaleString('en-US', {
				minimumFractionDigits: 2,
				maximumFractionDigits: 2
			}) : null 
			},
			daily_billed_kwhrs: { value: device.daily_billed_kwhrs != null ? Number(device.daily_billed_kwhrs).toLocaleString('en-US', {
				minimumFractionDigits: 2,
				maximumFractionDigits: 2
			}) : null 
			},
			total_usage_time: {
				value: this.commonService.convertSecondsToHoursMinsSecs(
					device.total_consumed_usage_time || 0
				)
			},
			included_in_reports: {
				value: device.included_in_reports == true ? 'Yes' : 'No', 
				link: device.serial_number
					? ['/', this.customerId, this.siteId, device.mac_address, 'settings']
					: null,
					action: () => {
						this.router.navigate(
							['/', this.customerId, this.siteId, device.mac_address, 'settings'], 
							{ queryParams: { tab: 'official-reporting' } }
						);
					}
			},
		};
	}

	preparecolumnConfig() {
		let columnConfig: ColumnsConfig[] = [
			{ key: "serial_number", name: this.translateService.instant("g.serial_number"), type: 'link' },
		];

		columnConfig.push({ key: "panel_id", name: this.translateService.instant("iotemeter.devices.panel_id"), type: 'string' });

		columnConfig.push(
			{ key: 'load_id', name: this.translateService.instant('iotemeter.devices.load_id'), type: 'string'}
		);

		columnConfig.push(
			{ key: 'installation_date', name: this.translateService.instant('iotemeter.devices.installation_date'), type: 'date'},
		);

		columnConfig.push(
			{ key: 'load_type', name: this.translateService.instant('iotemeter.devices.load_type'), type: 'string' },
			{ key: "billed_kwhrs", name: this.translateService.instant("iotemeter.performance_analytics.billed_kwhrs"), type: 'number'},
			{ key: "daily_billed_kwhrs", name: this.translateService.instant("iotemeter.devices.daily_billed_kwhrs"), type: 'number'},
			{ key: "total_usage_time", name: this.translateService.instant("iotemeter.devices.total_usage_time"), type: 'number' },
		);

		if (this.usersService.hasAccessFunction('official_reporting')) {
			columnConfig.push(
				{ key: "included_in_reports", name: this.translateService.instant("smart_rebates_reports.included_in_reports"), type: 'action_link' },
			);
		}

		this.imUsageReportTableConfig.column = columnConfig;
	}
}
